import axios from '../utils/axiosConfig';
import { SnippetAlbum, SnippetMedia, AlbumWithMedia, ApiResponse, ApiListResponse } from '../types/contentManagement';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class SnippetAlbumService {
  private albumURL = `${API_BASE_URL}/snippet-albums`;
  private mediaURL = `${API_BASE_URL}/snippet-media`;

  // =====================================================
  // ALBUM OPERATIONS
  // =====================================================

  // Get all albums
  async getAllAlbums(includeHidden: boolean = false): Promise<SnippetAlbum[]> {
    try {
      const url = includeHidden ? `${this.albumURL}?includeHidden=true` : this.albumURL;
      const response = await axios.get<ApiListResponse<SnippetAlbum>>(url);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch albums');
    } catch (error) {
      console.error('Error fetching albums:', error);
      throw error;
    }
  }

  // Get visible albums (for homepage)
  async getVisibleAlbums(): Promise<SnippetAlbum[]> {
    return this.getAllAlbums(false);
  }

  // Get album by ID
  async getAlbumById(id: number): Promise<SnippetAlbum> {
    try {
      const response = await axios.get<ApiResponse<SnippetAlbum>>(`${this.albumURL}/${id}`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch album');
    } catch (error) {
      console.error('Error fetching album:', error);
      throw error;
    }
  }

  // Get album with all its media
  async getAlbumWithMedia(id: number): Promise<AlbumWithMedia> {
    try {
      const response = await axios.get<ApiResponse<AlbumWithMedia>>(`${this.albumURL}/${id}/media`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch album with media');
    } catch (error) {
      console.error('Error fetching album with media:', error);
      throw error;
    }
  }

  // Create new album
  async createAlbum(formData: FormData): Promise<SnippetAlbum> {
    try {
      const response = await axios.post<ApiResponse<SnippetAlbum>>(this.albumURL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create album');
    } catch (error) {
      console.error('Error creating album:', error);
      throw error;
    }
  }

  // Update album
  async updateAlbum(id: number, formData: FormData): Promise<SnippetAlbum> {
    try {
      const response = await axios.put<ApiResponse<SnippetAlbum>>(`${this.albumURL}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update album');
    } catch (error) {
      console.error('Error updating album:', error);
      throw error;
    }
  }

  // Delete album
  async deleteAlbum(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<null>>(`${this.albumURL}/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete album');
      }
    } catch (error) {
      console.error('Error deleting album:', error);
      throw error;
    }
  }

  // Toggle album visibility
  async toggleAlbumVisibility(id: number): Promise<SnippetAlbum> {
    try {
      const response = await axios.patch<ApiResponse<SnippetAlbum>>(`${this.albumURL}/${id}/toggle-visibility`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to toggle visibility');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  }

  // =====================================================
  // MEDIA OPERATIONS
  // =====================================================

  // Get all media (optionally filtered by album)
  async getAllMedia(albumId?: number | null, includeHidden: boolean = false): Promise<SnippetMedia[]> {
    try {
      let url = this.mediaURL;
      const params = new URLSearchParams();
      
      if (albumId !== undefined) {
        params.append('albumId', albumId === null ? 'null' : String(albumId));
      }
      if (includeHidden) {
        params.append('includeHidden', 'true');
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get<ApiListResponse<SnippetMedia>>(url);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch media');
    } catch (error) {
      console.error('Error fetching media:', error);
      throw error;
    }
  }

  // Get media without album (Others category)
  async getMediaWithoutAlbum(includeHidden: boolean = false): Promise<SnippetMedia[]> {
    return this.getAllMedia(null, includeHidden);
  }

  // Get media by album ID
  async getMediaByAlbum(albumId: number, includeHidden: boolean = false): Promise<SnippetMedia[]> {
    return this.getAllMedia(albumId, includeHidden);
  }

  // Upload multiple media files to an album
  async uploadMedia(formData: FormData): Promise<SnippetMedia[]> {
    try {
      const response = await axios.post<ApiListResponse<SnippetMedia>>(this.mediaURL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to upload media');
    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    }
  }

  // Update media
  async updateMedia(id: number, data: Partial<SnippetMedia>): Promise<SnippetMedia> {
    try {
      const response = await axios.put<ApiResponse<SnippetMedia>>(`${this.mediaURL}/${id}`, data);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update media');
    } catch (error) {
      console.error('Error updating media:', error);
      throw error;
    }
  }

  // Delete media
  async deleteMedia(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<null>>(`${this.mediaURL}/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete media');
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      throw error;
    }
  }

  // Toggle media visibility
  async toggleMediaVisibility(id: number): Promise<SnippetMedia> {
    try {
      const response = await axios.patch<ApiResponse<SnippetMedia>>(`${this.mediaURL}/${id}/toggle-visibility`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to toggle visibility');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  }

  // =====================================================
  // HELPER METHODS
  // =====================================================

  // Get file URL
  getFileUrl(filePath: string | undefined | null): string {
    if (!filePath || typeof filePath !== 'string') {
      return '';
    }
    
    if (filePath.startsWith('http')) {
      return filePath;
    }
    
    if (filePath.startsWith('/uploads/')) {
      return filePath;
    }
    
    return filePath;
  }

  // Get media icon
  getMediaIcon(mediaType: 'image' | 'video'): string {
    return mediaType === 'image' ? '🖼️' : '🎥';
  }
}

export default new SnippetAlbumService();
